import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Conversation, PresenceStatus, UserBrief } from "@/messaging/types";
import { Composer, type ComposerHandle } from "./Composer";
import { EmptyState } from "./EmptyState";
import { Lightbox } from "./Lightbox";
import { MessageRow } from "./MessageRow";
import { TypingIndicator } from "./TypingIndicator";
import { AlertIcon, ArrowDownIcon, ChatIcon } from "./icons";
import { dayLabel, isNewDay } from "./time";
import { useMessages, type ChatMessage } from "./useMessages";

const NEAR_BOTTOM_PX = 120;

export function MessageThread({
  conversation,
  myId,
  presenceOf,
  open,
}: {
  conversation: Conversation;
  myId: string | null;
  presenceOf: (u: UserBrief | null | undefined) => PresenceStatus;
  /** Whether the drawer is actually visible. The thread stays MOUNTED while
   *  the drawer is closed (it's translated off-screen for the animation), so
   *  without this it would auto-mark-read incoming messages even when hidden —
   *  wiping the unread badge the instant a message arrives. */
  open: boolean;
}) {
  const convId = conversation.id;
  const openRef = useRef(open);
  openRef.current = open;
  const {
    messages,
    state,
    hasMore,
    loadingOlder,
    loadOlder,
    reload,
    send,
    retry,
    editMessage,
    deleteMessage,
    toggleReaction,
    markRead,
    typing,
    pingTyping,
  } = useMessages(convId);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<ComposerHandle | null>(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [reply, setReply] = useState<ChatMessage | null>(null);

  const prevLenRef = useRef(0);
  const prevHeightRef = useRef(0);
  const restoringRef = useRef(false);
  const didInitialRef = useRef(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setShowJump(false);
    setNewCount(0);
  }, []);

  // reset transient state on conversation switch
  useEffect(() => {
    setReply(null);
    atBottomRef.current = true;
    prevLenRef.current = 0;
    didInitialRef.current = false;
  }, [convId]);

  // scroll management: first paint jumps to bottom; later pages restore
  // position; new messages pin-or-pill depending on where we are.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    if (restoringRef.current) {
      // an older page was prepended — keep the viewport anchored
      el.scrollTop = el.scrollHeight - prevHeightRef.current;
      restoringRef.current = false;
      prevLenRef.current = messages.length;
      return;
    }

    if (!didInitialRef.current) {
      if (messages.length > 0) {
        el.scrollTop = el.scrollHeight;
        didInitialRef.current = true;
      }
      prevLenRef.current = messages.length;
      return;
    }

    if (messages.length > prevLenRef.current) {
      const last = messages[messages.length - 1];
      const mineNew = last?.sender?.id === myId;
      if (atBottomRef.current || mineNew) {
        el.scrollTop = el.scrollHeight;
        setShowJump(false);
        setNewCount(0);
      } else {
        setNewCount((n) => n + (messages.length - prevLenRef.current));
        setShowJump(true);
      }
    }
    prevLenRef.current = messages.length;
  }, [messages, myId]);

  useEffect(() => {
    // Only mark read while the drawer is actually visible — otherwise a
    // background (translated-off-screen) thread would clear the unread badge
    // the moment a message arrives.
    if (open && state === "ready" && atBottomRef.current) markRead();
  }, [messages, state, markRead, open]);

  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = dist < NEAR_BOTTOM_PX;
    atBottomRef.current = near;
    setShowJump(!near);
    if (near && openRef.current) {
      setNewCount(0);
      markRead();
    }
  }, [markRead]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const el = scrollerRef.current;
    if (!sentinel || !el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingOlder) {
          prevHeightRef.current = el.scrollHeight;
          restoringRef.current = true;
          void loadOlder();
        }
      },
      { root: el, rootMargin: "120px 0px 0px 0px" },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadingOlder, loadOlder]);

  // last own message → seen by others?
  const lastOwn = [...messages].reverse().find((m) => m.sender?.id === myId && !m.localId);
  const seenByUsers = lastOwn
    ? conversation.members
        .filter(
          (mem) =>
            mem.user.id !== myId &&
            mem.last_read_at != null &&
            new Date(mem.last_read_at).getTime() >= new Date(lastOwn.created_at).getTime(),
        )
        .map((mem) => mem.user)
    : [];
  const seenLastOwn = seenByUsers.length > 0;

  const myLastReadAt = conversation.members.find((m) => m.user.id === myId)?.last_read_at ?? null;
  const firstUnreadId =
    conversation.unread_count > 0 && myLastReadAt
      ? messages.find(
          (m) =>
            m.sender?.id !== myId &&
            new Date(m.created_at).getTime() > new Date(myLastReadAt).getTime(),
        )?.id ?? null
      : null;

  const isGroup = conversation.kind === "group";

  return (
    <div
      className="relative flex flex-1 min-h-0 flex-col bg-ink-950"
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files.length) composerRef.current?.addFiles(e.dataTransfer.files);
      }}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin py-2"
      >
        {state === "loading" && (
          <div className="space-y-3 px-4 py-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`h-9 rounded-2xl bg-white/[0.04] animate-pulse ${
                  i % 2 ? "ml-auto w-1/2" : "w-2/3"
                }`}
              />
            ))}
          </div>
        )}

        {state === "error" && (
          <EmptyState
            icon={<AlertIcon />}
            title="Couldn't load messages"
            hint="Something went wrong fetching this conversation."
            action={
              <button className="btn-secondary !py-1.5 !text-xs" onClick={reload}>
                Try again
              </button>
            }
          />
        )}

        {state === "ready" && messages.length === 0 && (
          <EmptyState
            icon={<ChatIcon />}
            title="No messages yet"
            hint="Say hi — your first message starts the conversation."
          />
        )}

        {state === "ready" && messages.length > 0 && (
          <>
            <div ref={topSentinelRef} />
            {loadingOlder && (
              <div className="py-2 text-center text-[11px] text-slate-500">Loading…</div>
            )}
            {messages.map((m, i) => {
              const prev = messages[i - 1] ?? null;
              const showDay = isNewDay(prev?.created_at ?? null, m.created_at);
              const mine = m.sender?.id === myId;
              const runBreak =
                !prev ||
                prev.sender?.id !== m.sender?.id ||
                !!prev.system_kind ||
                !!m.system_kind ||
                showDay;
              return (
                <div key={m.id}>
                  {showDay && <DaySeparator label={dayLabel(m.created_at)} />}
                  {m.id === firstUnreadId && <UnreadDivider />}
                  {m.system_kind ? (
                    <SystemNotice message={m} />
                  ) : (
                    <MessageRow
                      message={m}
                      mine={mine}
                      showAvatar={runBreak}
                      showName={isGroup && runBreak && !mine}
                      senderStatus={presenceOf(m.sender)}
                      onOpenImage={setLightbox}
                      onReply={setReply}
                      onEdit={editMessage}
                      onDelete={deleteMessage}
                      onReact={toggleReaction}
                      onRetry={retry}
                      seen={m.id === lastOwn?.id ? seenLastOwn : undefined}
                      seenBy={m.id === lastOwn?.id ? seenByUsers : undefined}
                      animate={i === messages.length - 1}
                    />
                  )}
                </div>
              );
            })}
            <div className="h-1" />
          </>
        )}
      </div>

      <TypingIndicator users={typing} />

      {showJump && (
        <button
          onClick={() => scrollToBottom("smooth")}
          className="absolute right-4 bottom-24 z-10 inline-flex items-center gap-1.5 rounded-full bg-ink-800 border border-white/10 shadow-lg px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-750 transition"
        >
          {newCount > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-white tabular-nums">
              {newCount}
            </span>
          )}
          <ArrowDownIcon size={15} />
        </button>
      )}

      <Composer
        ref={composerRef}
        replyTo={reply}
        onCancelReply={() => setReply(null)}
        onSend={(input) => {
          send(input);
          setReply(null);
        }}
        onTyping={pingTyping}
      />

      {dragOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-accent-500/10 border-2 border-dashed border-accent-500/50 backdrop-blur-[1px] pointer-events-none">
          <span className="text-sm font-medium text-accent-200">Drop to send</span>
        </div>
      )}

      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-3">
      <span className="rounded-full bg-white/[0.05] border border-white/5 px-2.5 py-0.5 text-[11px] font-medium text-slate-400">
        {label}
      </span>
    </div>
  );
}

function UnreadDivider() {
  return (
    <div className="flex items-center gap-2 my-2 px-4">
      <div className="h-px flex-1 bg-accent-500/30" />
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-accent-300">
        New
      </span>
      <div className="h-px flex-1 bg-accent-500/30" />
    </div>
  );
}

function SystemNotice({ message }: { message: ChatMessage }) {
  return (
    <div className="flex justify-center my-1.5 px-4">
      <span className="max-w-[85%] truncate rounded-full bg-white/[0.04] border border-white/5 px-3 py-0.5 text-[11px] text-slate-400">
        {message.body}
      </span>
    </div>
  );
}
